import { Component, EventEmitter, Input, Output, QueryList, ViewChildren } from '@angular/core';

import { Analysis, PluginsForAnalysisResponse } from '../../../interfaces/analysis';
import { AnalysisContextActions, DownloadType, LoadingState } from '../analysis.types';
import { InfoPanelComponent } from '../../../shared/info-panel/info-panel.component';
import { InfoPanelExpansionSectionComponent } from '../../../shared/info-panel/expansion-section-component/info-panel-expansion-section.component';
import { InfoPanelFieldComponent } from '../../../shared/info-panel/field-component/info-panel-field.component';
import { InfoPanelLinkComponent } from '../../../shared/info-panel/link-component/info-panel-link.component';
import { UtcDatePipe } from '../../../shared/pipes/utc-date.pipe';
import { InfoPanelSectionComponent } from '../../../shared/info-panel/section-component/info-panel-section.component';

@Component({
  selector: 'app-analysis-info-panel',
  standalone: true,
  imports: [
    InfoPanelComponent,
    InfoPanelExpansionSectionComponent,
    InfoPanelFieldComponent,
    InfoPanelLinkComponent,
    UtcDatePipe,
    InfoPanelSectionComponent,
  ],
  templateUrl: './analysis-info-panel.component.html',
})
export class AnalysisInfoPanelComponent {
  @ViewChildren(InfoPanelExpansionSectionComponent) private sections!: QueryList<InfoPanelExpansionSectionComponent>;

  @Input() analysis: Analysis | null = null;
  @Input() contextActions: AnalysisContextActions | null = null;
  @Input() downloads: DownloadType[] = [];

  private _isOpen = false;
  @Input() set isOpen(value: boolean) {
    this._isOpen = value;
    if (!value) {
      setTimeout(() => this.sections?.forEach((s) => s.reset()), 225);
    }
  }
  get isOpen(): boolean {
    return this._isOpen;
  }

  @Output() closed = new EventEmitter<void>();
  @Output() viewResultClicked = new EventEmitter<void>();
  @Output() pluginLaunched = new EventEmitter<string>();
  @Output() downloadRequested = new EventEmitter<DownloadType>();

  readonly downloadMeta: Record<DownloadType, { label: string; text: string }> = {
    results: { label: $localize`Results`, text: $localize`Results` },
    logs: { label: $localize`Logs`, text: $localize`Logs` },
    raw: { label: $localize`Raw Data`, text: $localize`Raw Data` },
  };

  get title(): string {
    return this.analysis ? $localize`Analysis #${this.analysis.AnalysisID}` : $localize`Analysis`;
  }
  protected readonly LoadingState = LoadingState;

  get groupNames(): string | null {
    return this.analysis?.Groups.map((g) => g.GroupName).join(', ') || null;
  }

  get curateOptionsState(): LoadingState | undefined | 'empty' {
    const ca = this.contextActions;

    if (!ca) {
      return undefined;
    }

    if (ca.viewResult.status === LoadingState.Loading || ca.plugins.status === LoadingState.Loading) {
      return LoadingState.Loading;
    }

    const hasViewResult = ca.viewResult.status === LoadingState.Ready && !!ca.viewResult.url;
    const hasPlugins = ca.plugins.status === LoadingState.Ready && ca.plugins.items.length > 0;
    const stateResult = hasViewResult || hasPlugins ? LoadingState.Ready : 'empty';
    return stateResult;
  }

  get viewResultHref(): string | undefined {
    const vr = this.contextActions?.viewResult;
    if (vr?.status === LoadingState.Ready && vr.url && this.analysis?.AnalysisID) {
      return `${vr.url}/${this.analysis.AnalysisID}`;
    }
    return undefined;
  }

  get plugins(): PluginsForAnalysisResponse[] | undefined {
    const plugins = this.contextActions?.plugins;
    if (plugins?.status === LoadingState.Ready && plugins?.items.length > 0) {
      return plugins.items;
    }
    return undefined;
  }

  pluginHref(pluginId: string): string | undefined {
    if (!this.analysis?.AnalysisID) return undefined;
    return `/plugin-viewer/${pluginId}?analysis_id=${this.analysis.AnalysisID}`;
  }
}
